import React, { useEffect, useState } from "react";
import OwnerLayout from "@/components/OwnerLayout";
import { api, formatRupiah } from "@/lib/api";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

export default function OptionGroupsPage() {
  const [groups, setGroups] = useState([]);
  const [modal, setModal] = useState({ open: false, editing: null });

  const load = async () => {
    const { data } = await api.get("/option-groups");
    setGroups(data);
  };
  useEffect(() => { load(); }, []);

  const del = async (g) => {
    if (!window.confirm(`Hapus grup opsi "${g.name}"?`)) return;
    await api.delete(`/option-groups/${g.id}`);
    toast.success("Grup opsi dihapus");
    load();
  };

  return (
    <OwnerLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#1E3A2B]">Grup Opsi</h1>
          <p className="text-[#5C6A61] mt-1">Buat varian/tambahan yang bisa dilampirkan ke menu (level pedas, ukuran, topping, dll.)</p>
        </div>
        <button
          data-testid="btn-add-option-group"
          onClick={() => setModal({ open: true, editing: null })}
          className="bg-[#C25934] text-white hover:bg-[#A94726] px-4 py-2.5 rounded-xl text-sm font-medium inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Grup Opsi
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="option-groups-list">
        {groups.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#5C6A61] border border-dashed border-[#D3CEC4] rounded-md">
            Belum ada grup opsi.
          </div>
        )}
        {groups.map((g) => (
          <div key={g.id} className="bg-white rounded-md border border-[#D3CEC4] p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-medium text-[#1E3A2B]">{g.name}</div>
                <div className="text-xs text-[#5C6A61]">Min: {g.min_select} · Max: {g.max_select}</div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`btn-edit-og-${g.id}`} onClick={() => setModal({ open: true, editing: g })} className="p-2 hover:bg-[#F9F9F6] rounded-md"><Pencil className="w-4 h-4 text-[#5C6A61]" /></button>
                <button data-testid={`btn-delete-og-${g.id}`} onClick={() => del(g)} className="p-2 hover:bg-[#F9F9F6] rounded-md"><Trash2 className="w-4 h-4 text-[#D64045]" /></button>
              </div>
            </div>
            <div className="space-y-1.5">
              {g.options.map((o) => (
                <div key={o.id} className="flex items-center justify-between text-sm py-1.5 px-2 bg-[#F9F9F6] rounded-md">
                  <span className="text-[#1E3A2B]">{o.name}</span>
                  <span className="text-[#5C6A61]">{o.price_delta > 0 ? `+${formatRupiah(o.price_delta)}` : (o.price_delta < 0 ? formatRupiah(o.price_delta) : "-")}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <OptionGroupModal
        open={modal.open}
        editing={modal.editing}
        onClose={() => setModal({ open: false, editing: null })}
        onSaved={() => { setModal({ open: false, editing: null }); load(); }}
      />
    </OwnerLayout>
  );
}

function OptionGroupModal({ open, editing, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [minSel, setMinSel] = useState(0);
  const [maxSel, setMaxSel] = useState(1);
  const [options, setOptions] = useState([{ id: Math.random().toString(), name: "", price_delta: 0 }]);

  useEffect(() => {
    if (open) {
      setName(editing?.name || "");
      setMinSel(editing?.min_select ?? 0);
      setMaxSel(editing?.max_select ?? 1);
      setOptions(editing?.options?.length ? editing.options : [{ id: Math.random().toString(), name: "", price_delta: 0 }]);
    }
  }, [open, editing]);

  if (!open) return null;

  const save = async (e) => {
    e.preventDefault();
    const cleaned = options.filter((o) => o.name.trim());
    if (cleaned.length === 0) return toast.error("Tambahkan minimal satu opsi");
    const payload = { name, min_select: Number(minSel), max_select: Number(maxSel), options: cleaned.map((o) => ({ ...o, price_delta: Number(o.price_delta) || 0 })) };
    try {
      if (editing) await api.put(`/option-groups/${editing.id}`, payload);
      else await api.post("/option-groups", payload);
      toast.success("Grup opsi disimpan");
      onSaved();
    } catch { toast.error("Gagal menyimpan"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="modal-og">
      <div className="bg-white rounded-md max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-semibold text-[#1E3A2B]">{editing ? "Edit Grup Opsi" : "Tambah Grup Opsi"}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#5C6A61]" /></button>
        </div>
        <form onSubmit={save} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <label className="block">
            <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Nama Grup</span>
            <input data-testid="input-og-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5" placeholder="Contoh: Level Pedas, Ukuran" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Min. Pilih</span>
              <input data-testid="input-og-min" type="number" min={0} value={minSel} onChange={(e) => setMinSel(e.target.value)} className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Max. Pilih</span>
              <input data-testid="input-og-max" type="number" min={1} value={maxSel} onChange={(e) => setMaxSel(e.target.value)} className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5" />
            </label>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Opsi</span>
              <button type="button" data-testid="btn-add-option" onClick={() => setOptions([...options, { id: Math.random().toString(), name: "", price_delta: 0 }])} className="text-xs text-[#C25934] inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Tambah</button>
            </div>
            <div className="space-y-2">
              {options.map((o, idx) => (
                <div key={o.id} className="flex items-center gap-2">
                  <input
                    data-testid={`input-opt-name-${idx}`}
                    placeholder="Nama opsi"
                    value={o.name}
                    onChange={(e) => setOptions(options.map((x) => x.id === o.id ? { ...x, name: e.target.value } : x))}
                    className="flex-1 border border-[#D3CEC4] rounded-xl px-3 py-2"
                  />
                  <input
                    data-testid={`input-opt-price-${idx}`}
                    type="number"
                    placeholder="Harga tambahan"
                    value={o.price_delta}
                    onChange={(e) => setOptions(options.map((x) => x.id === o.id ? { ...x, price_delta: e.target.value } : x))}
                    className="w-32 border border-[#D3CEC4] rounded-xl px-3 py-2"
                  />
                  <button type="button" data-testid={`btn-remove-opt-${idx}`} onClick={() => setOptions(options.filter((x) => x.id !== o.id))} className="p-2"><Trash2 className="w-4 h-4 text-[#D64045]" /></button>
                </div>
              ))}
            </div>
          </div>
          <button data-testid="btn-save-og" type="submit" className="w-full bg-[#C25934] text-white py-2.5 rounded-xl font-medium">Simpan</button>
        </form>
      </div>
    </div>
  );
}
